
import { Component, Vue } from "vue-property-decorator";
import {
  Activity,
  ActivityType,
  ProgramFormula,
  Phase,
  Program,
  ProgramType,
} from "@/types";
import { ROOT_ACTIONS } from "@/store/actions";
import { programTypes } from "@/variables";
import { activityTypes } from "@/variables";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "@/firebase";

@Component
export default class EditTemplate extends Vue {
  PHASE_COUNT = 3;

  phaseTab = 0;
  _activityTypes = activityTypes;
  _programTypes = programTypes;

  phases = [] as Phase[];

  activities = [] as Activity[];

  getHoursForPhase(phase: Phase): number {
    const hours = this.activities
      .filter((e) => e.phaseId === phase.id)
      .map((e) => e.plannedHours)
      .reduce((a, b) => a! + b!, 0);
    if (isNaN(hours as number)) return 0;
    return hours as number;
  }

  addNewActivityToPhase(phaseId?: string) {
    if (!phaseId) return;
    this.activities.push({
      name: "",
      description: "",
      type: ActivityType.INDIVIDUAL_COACHING,
      isOnline: false,
      plannedHours: 0,
      end: null,
      start: new Date(),
      required: false,
      phaseId: phaseId,
    });
  }

  program: Partial<Program> = {
    title: "",
    description: "",
    formula: ProgramFormula.ALGEMEEN_OPZEG,
    type: ProgramType.GROUP,
    language: "nl-be",
  };

  loading = {
    program: true,
    submit: false,
  };

  headers = [
    { text: "Titel", value: "title" },
    { text: "Type", value: "type" },
  ];

  async mounted() {
    const programDoc = await getDoc(
      doc(firestore, "program-templates", this.$route.params.id)
    );
    const phasesDocs = await getDocs(
      query(
        collection(firestore, "phases"),
        where("programId", "==", this.$route.params.id),
        orderBy("index", "asc")
      )
    );

    for (let phaseDoc of phasesDocs.docs) {
      this.phases.push({
        ...phaseDoc.data(),
        id: phaseDoc.id,
      } as Phase);
    }

    const activityDocs = await getDocs(collection(firestore, "activities"));
    const activities = activityDocs.docs.map(
      (e) => ({ ...e.data(), id: e.id } as Activity)
    );
    for (let activity of activities) {
      this.activities.push(activity);
    }

    const program = {
      ...programDoc.data(),
      language: programDoc.data()!.language || "nl-be",
      id: programDoc.id,
    };

    this.program = program;
    this.loading.program = false;
  }

  validateInputs(): boolean {
    if (this.program.title == "") return false;
    return true;
  }

  async submit() {
    try {
      console.log("[DEBUG] Submit function called with:", {
        program: this.program,
        phases: this.phases,
        activities: this.activities,
      });

      if (!this.validateInputs()) {
        console.log("[DEBUG] Input validation failed");
        this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
          color: "warning",
          text: "Ongeldige invoer",
        });
        return;
      }

      console.log("[DEBUG] Input validation passed");
      this.loading.submit = true;

      console.log("[DEBUG] Setting phase hours");
      this.phases.forEach((phase) => {
        phase.hours = this.getHoursForPhase(phase);
        console.log(`[DEBUG] Phase ${phase.id} hours set to ${phase.hours}`);
      });

      console.log("[DEBUG] Updating program template:", this.program);
      await updateDoc(
        doc(firestore, "program-templates", this.$route.params.id),
        {
          title: this.program.title,
          description: this.program.description || "",
          formula: this.program.formula || ProgramFormula.OTHER,
          type: this.program.type,
          language: this.program.language,
        }
      );
      console.log("[DEBUG] Program template updated successfully");

      for (let phase of this.phases) {
        console.log(`[DEBUG] Updating phase ${phase.id}:`, phase);
        await updateDoc(doc(firestore, "phases", phase.id as string), {
          title: phase.title || "",
          description: phase.description || "",
          hours: this.getHoursForPhase(phase),
          screencastHours: phase.screencastHours || 0,
          webinarHours: phase.webinarHours || 0
        });
        console.log(`[DEBUG] Phase ${phase.id} updated successfully`);

        const phaseActivities = this.activities.filter(
          (e) => e.phaseId == phase.id
        );
        console.log(
          `[DEBUG] Found ${phaseActivities.length} activities for phase ${phase.id}`
        );

        for (let activity of phaseActivities) {
          console.log(`[DEBUG] Updating activity ${activity.id}:`, activity);
          if (activity.id) {
            await updateDoc(
              doc(firestore, "activities", activity.id as unknown as string),
              {
                name: activity.name || "",
                description: activity.description || "",
                type: activity.type || ActivityType.INDIVIDUAL_COACHING,
                plannedHours: activity.plannedHours,
              }
            );
          } else {
            //create new activity
            await addDoc(collection(firestore, "activities"), {
              name: activity.name || "",
              description: activity.description || "",
              type: activity.type || ActivityType.INDIVIDUAL_COACHING,
              plannedHours: activity.plannedHours,
              phaseId: phase.id,
            });
          }

          console.log(`[DEBUG] Activity ${activity.id} updated successfully`);
        }
      }

      console.log("[DEBUG] Template update completed successfully");
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "success",
        text: "Template aangepast",
      });

      this.$router.push("/admin/presence/templates");
    } catch (error) {
      console.error("[DEBUG] Error in submit function:", error);
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: error,
      });
    } finally {
      console.log("[DEBUG] Submit function completed");
      this.loading.submit = false;
    }
  }

  languages = [
    {
      text: "Nederlands",
      value: "nl-be",
    },
    {
      text: "Frans",
      value: "fr",
    },
  ];

  confirmDialog = false;
  deleteLoading = false;

  async deleteTemplate() {
    try {
      this.deleteLoading = true;
      await deleteDoc(
        doc(firestore, "program-templates", this.$route.params.id)
      );
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "success",
        text: "Template verwijderd",
      });
      this.$router.push("/admin/presence/templates");
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: error,
      });
    } finally {
      this.deleteLoading = false;
    }
  }
}
